/**
 * Der ENTWURF eines Layouts als unveränderliches Objekt (Layout-Editor Etappe 1). Die
 * Oberfläche kennt die Domäne nicht (Hausregel) — hier stehen nur Lesen/Setzen an einem
 * Pfad, die Umwandlung Zahl ↔ „18mm", der Vergleich und der Massstab eines Seitenbildes.
 * Die WHITELIST prüft der Server; seine Befunde (Pfade) landen über `feldFehlerKarte` am
 * Feld.
 */

export type Entwurf = Record<string, unknown>;

function istObjekt(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === "object" && wert !== null && !Array.isArray(wert);
}

export function wertAn(entwurf: Entwurf, pfad: readonly string[]): unknown {
  let knoten: unknown = entwurf;
  for (const teil of pfad) {
    if (!istObjekt(knoten)) return undefined;
    knoten = knoten[teil];
  }
  return knoten;
}

/** Setzt einen Wert (bei `undefined`: entfernt ihn) — leere Objekte fallen weg, damit
 *  „Systemwert" auch im Entwurf wirklich „nichts" heisst. */
export function mitWert(entwurf: Entwurf, pfad: readonly string[], wert: unknown): Entwurf {
  const [kopf, ...rest] = pfad;
  if (kopf === undefined) return entwurf;
  const kopie: Entwurf = { ...entwurf };
  if (rest.length === 0) {
    if (wert === undefined) delete kopie[kopf];
    else kopie[kopf] = wert;
    return kopie;
  }
  const unter = istObjekt(kopie[kopf]) ? kopie[kopf] as Entwurf : {};
  const neu = mitWert(unter, rest, wert);
  if (Object.keys(neu).length === 0) delete kopie[kopf];
  else kopie[kopf] = neu;
  return kopie;
}

export function zahlAus(wert: unknown, einheit: "mm" | "pt" | "%"): string {
  return typeof wert === "string" && wert.endsWith(einheit) ? wert.slice(0, -einheit.length) : "";
}

/** Aus der Eingabe „18,5" wird „18.5mm"; leer heisst „Systemwert" (undefined). Was keine
 *  Zahl ist, geht trotzdem hinaus — der Server sagt am Feld, was erlaubt ist. */
export function mitEinheit(eingabe: string, einheit: "mm" | "pt" | "%"): string | undefined {
  const roh = eingabe.trim().replace(",", ".");
  return roh === "" ? undefined : `${roh}${einheit}`;
}

export function feldFehlerKarte(
  felder: readonly { field: string; message: string }[] | undefined,
): Record<string, string> {
  const karte: Record<string, string> = {};
  for (const f of felder ?? []) {
    if (karte[f.field] === undefined) karte[f.field] = f.message;
  }
  return karte;
}

function kanonisch(wert: unknown): string {
  if (Array.isArray(wert)) return `[${wert.map(kanonisch).join(",")}]`;
  if (istObjekt(wert)) {
    return `{${Object.keys(wert).sort()
      .map((k) => `${JSON.stringify(k)}:${kanonisch(wert[k])}`).join(",")}}`;
  }
  return JSON.stringify(wert);
}

export function istGleich(a: unknown, b: unknown): boolean {
  return kanonisch(a) === kanonisch(b);
}

/** Millimeter je Bildpunkt eines gezeichneten Seitenbildes (A4 = 210 mm breit). */
export function mmJePixel(breitePx: number): number {
  return breitePx > 0 ? 210 / breitePx : 0;
}

export function aufZehntel(mm: number): number {
  return Math.round(mm * 10) / 10;
}

/** Der Wert an einem BEFUND-Pfad des Servers — `positionsspalten[1].breite`,
 *  `seitenraender.oben` (Punkte trennen Schlüssel, `[n]` greift in Listen). */
export function wertAmPfad(entwurf: Entwurf, pfad: string): unknown {
  let knoten: unknown = entwurf;
  for (const teil of pfad.split(/\.|\[(\d+)\]/).filter((t) => t !== undefined && t !== "")) {
    if (Array.isArray(knoten) && /^\d+$/.test(teil)) knoten = knoten[Number(teil)];
    else if (istObjekt(knoten)) knoten = knoten[teil];
    else return undefined;
  }
  return knoten;
}

/**
 * Befunde, die von MEHR als ihrem eigenen Feld abhängen (Belegdesign Etappe 2): Die Lage des
 * Logos und der Kopfbereich prüft der Server gemeinsam (Kopplung, „beginnt unter dem
 * Kopfbereich“) — ein solcher Befund ist auch erledigt, wenn sich der Partner ändert.
 */
const MITGEPRUEFT: Readonly<Record<string, readonly string[]>> = {
  logo: ["kopfbereich"],
  kopfbereich: ["logo"],
};

/** Feldfehler, deren Feld sich zwischen `alt` und `neu` geändert hat, fallen weg — ein
 *  Befund gilt dem Wert, den der Server gesehen hat, nicht der Korrektur. Bei Logo und
 *  Kopfbereich zählt auch der Partner (`MITGEPRUEFT`). */
export function ohneGeaenderte(
  fehler: Record<string, string>, alt: Entwurf, neu: Entwurf,
): Record<string, string> {
  const rest: Record<string, string> = {};
  for (const [pfad, text] of Object.entries(fehler)) {
    const praefix = pfad.replace(/^layout\./, "").split(/[.[]/)[0] ?? "";
    const partner = Object.hasOwn(MITGEPRUEFT, praefix) ? MITGEPRUEFT[praefix]! : [];
    if (istGleich(wertAmPfad(alt, pfad), wertAmPfad(neu, pfad))
        && partner.every((p) => istGleich(wertAn(alt, [p]), wertAn(neu, [p])))) {
      rest[pfad] = text;
    }
  }
  return rest;
}

/** Eine Spalte der Positionstabelle, wie sie im Entwurf steht (ohne Beschriftung). */
export type Spalte = {
  feld: string; titel: string; breite: string; align: "left" | "right" | "center";
  aktiv: boolean;
};

/**
 * Die Spalten, die das Register zeigt: die eigene Liste des Entwurfs (sie ersetzt im
 * Overlay die Systemliste GANZ) oder der Katalog. Fehlt einer eigenen Liste ein
 * Katalogfeld, steht es inaktiv hinten — sonst liesse es sich nie wieder einschalten.
 * Felder, die der Katalog nicht kennt, fallen weg.
 */
export function spaltenAus(
  entwurf: Entwurf, katalog: readonly (Spalte & { label: string })[],
): Spalte[] {
  const bekannt = new Map(katalog.map((k) => [k.feld, k]));
  const eigen = wertAn(entwurf, ["positionsspalten"]);
  if (!Array.isArray(eigen)) {
    return katalog.map((k) => ({ feld: k.feld, titel: k.titel, breite: k.breite,
                                 align: k.align, aktiv: k.aktiv }));
  }
  const liste: Spalte[] = [];
  for (const s of eigen as Spalte[]) {
    if (istObjekt(s) && bekannt.has(s.feld) && !liste.some((l) => l.feld === s.feld)) {
      liste.push({ ...s });
    }
  }
  for (const k of katalog) {
    if (!liste.some((l) => l.feld === k.feld)) {
      liste.push({ feld: k.feld, titel: k.titel, breite: k.breite, align: k.align,
                   aktiv: false });
    }
  }
  return liste;
}

/** Summe der aktiven Breiten in % — in Hundertsteln gerechnet wie die Whitelist. */
export function breitenSumme(spalten: readonly Spalte[]): number {
  let hundertstel = 0;
  for (const s of spalten) {
    const zahl = Number(zahlAus(s.breite, "%"));
    if (s.aktiv && zahlAus(s.breite, "%") !== "" && Number.isFinite(zahl)) {
      hundertstel += Math.round(zahl * 100);
    }
  }
  return hundertstel / 100;
}

/**
 * Die Lagen der Spaltenlinien in % der Satzspiegelbreite (Entscheid 64): die Grenzen
 * zwischen den AKTIVEN Spalten in Listenreihenfolge, also die kumulierten Breiten ohne die
 * letzte (die fällt auf den rechten Rand). Spalten ohne Zahl zählen 0 und erzeugen keine
 * eigene Linie. Rein für die Vorschau-Überlagerung, nie im PDF.
 */
export function spaltenLinien(spalten: readonly { breite: string; aktiv?: boolean }[]): number[] {
  const aktive = spalten.filter((s) => s.aktiv !== false && zahlAus(s.breite, "%") !== ""
    && Number.isFinite(Number(zahlAus(s.breite, "%"))));
  const linien: number[] = [];
  let hundertstel = 0;
  for (const s of aktive.slice(0, -1)) {
    hundertstel += Math.round(Number(zahlAus(s.breite, "%")) * 100);
    linien.push(hundertstel / 100);
  }
  return linien;
}

/** Feld der Ausgleichsspalte (Entscheid Dirk 09.10.2026: „Bezeichnung“). */
export const AUSGLEICH_FELD = "text";

/**
 * Hält die Summe der aktiven Breiten auf 100 %, indem die Bezeichnung den Rest übernimmt
 * (Entscheid Dirk 09.10.2026): beim Ein-/Ausschalten einer Spalte und bei Breitenänderungen
 * ANDERER Spalten. Unangetastet bleibt die Liste, wenn die Bezeichnung selbst geändert wurde,
 * abgeschaltet ist, eine aktive Breite (noch) keine Zahl ist oder für sie weniger als 1 %
 * bliebe — dann zeigt die Summenzeile den Befund wie bisher.
 */
export function mitAusgleich(spalten: readonly Spalte[], geaendert: number): Spalte[] {
  const liste = [...spalten];
  const ziel = liste.findIndex((s) => s.feld === AUSGLEICH_FELD);
  if (ziel < 0 || ziel === geaendert || liste[ziel]?.aktiv !== true) return liste;
  let andere = 0;
  for (const [i, s] of liste.entries()) {
    if (i === ziel || !s.aktiv) continue;
    const roh = zahlAus(s.breite, "%");
    const zahl = Number(roh);
    if (roh === "" || !Number.isFinite(zahl)) return liste;
    andere += Math.round(zahl * 100);
  }
  const rest = 10000 - andere;
  if (rest < 100) return liste;
  liste[ziel] = { ...(liste[ziel] as Spalte), breite: `${rest / 100}%` };
  return liste;
}

// ---------------------------------------------------------------------------
// Das freie Logo (Belegdesign Etappe 2, Aufgabe 7)
// ---------------------------------------------------------------------------

/** Die Maße des Firmenlogos aus der Sicht (`unternehmen.logoMasse`, Gestalt wie der Server). */
export type LogoMasse =
  | { breitePx: number; hoehePx: number }
  | { viewBox: { breite: number; hoehe: number } | null }
  | null;

/** 96 dpi wie rechnungslayout und Engine: Pixel → mm. */
const PX_ZU_MM = 25.4 / 96;

function zahlOderNull(wert: unknown): number | null {
  return typeof wert === "number" && Number.isFinite(wert) ? wert : null;
}

/** Die Prozent des Logos im Entwurf — fehlt oder ungültig: 100 (so zeichnet der Rahmen,
 *  den Befund nennt der Server). */
export function logoProzent(entwurf: Entwurf): number {
  const p = zahlOderNull(wertAn(entwurf, ["logo", "prozent"]));
  return p !== null && p >= 25 && p <= 200 ? p : 100;
}

/** Die Kante des Logos in mm: Grundbreite (Katalog, 34 mm) × Prozent / 100. */
export function logoKanteMm(entwurf: Entwurf, grundbreiteMm: number): number {
  return grundbreiteMm * logoProzent(entwurf) / 100;
}

/**
 * Die EINGEPASSTE Höhe des Logos in mm bei der Kante `kante` — nur zum ZEICHNEN des
 * Ziehrahmens und für den Kopfbereich-Vorschlag. Maßgeblich prüft der Server (Kopplung am
 * Speichern) mit der Rechnung der Engine; diese Zeilen spiegeln sie: SVG `K × min(1, h/b)`
 * (ohne Verhältnis K), Rasterbild nie über die Eigengröße bei 96 dpi (Entscheid 68 a).
 * Ohne Maße zeichnet der Rahmen ein Quadrat (K).
 */
export function logoHoeheMm(kante: number, masse: LogoMasse): number {
  if (masse === null) return kante;
  if ("viewBox" in masse) {
    const vb = masse.viewBox;
    return vb === null || !(vb.breite > 0) || !(vb.hoehe > 0) ? kante
      : kante * Math.min(1, vb.hoehe / vb.breite);
  }
  if (!(masse.breitePx > 0) || !(masse.hoehePx > 0)) return kante;
  const eigenB = masse.breitePx * PX_ZU_MM;
  const eigenH = masse.hoehePx * PX_ZU_MM;
  return eigenH * Math.min(1, kante / eigenB, kante / eigenH);
}

/** Bis zu welcher Größe ein RASTERlogo noch wächst (Entscheid 68 a): darüber bleibt es bei
 *  seiner Eigengröße. `null` bei SVG oder ohne Maße (wächst frei). */
export function logoMaxProzent(masse: LogoMasse, grundbreiteMm: number): number | null {
  if (masse === null || "viewBox" in masse) return null;
  if (!(masse.breitePx > 0) || !(masse.hoehePx > 0) || !(grundbreiteMm > 0)) return null;
  return 100 * Math.max(masse.breitePx, masse.hoehePx) * PX_ZU_MM / grundbreiteMm;
}

/** Die freie Lage des Logos, wenn x UND y als Zahl im Entwurf stehen — sonst null
 *  (Textfluss oder ungültig). */
export function logoLage(entwurf: Entwurf): { xMm: number; yMm: number } | null {
  const x = zahlOderNull(wertAn(entwurf, ["logo", "xMm"]));
  const y = zahlOderNull(wertAn(entwurf, ["logo", "yMm"]));
  return x === null || y === null ? null : { xMm: x, yMm: y };
}

export type KopfSeite = "ersteSeiteMm" | "folgeseitenMm";

/** Die Unterkante des Kopfbereichs eines Seitentyps (mm ab Blattkante) oder null. */
export function kopfbereichMm(entwurf: Entwurf, seite: KopfSeite): number | null {
  return zahlOderNull(wertAn(entwurf, ["kopfbereich", seite]));
}

/** Was das Setzen der Lage braucht: der Vorschlag beim ersten Setzen (heutige Lage: Rand
 *  links/oben), die eingepasste Höhe und die Grenzen des Kopfbereichs. */
export type LogoKontext = {
  vorschlag: { xMm: number; yMm: number };
  hoeheMm: number;
  kopfMinMm: number;
  kopfMaxMm: number;
};

/** Der Kopfbereich-Vorschlag unter einem Logo bei `yMm`: y + eingepasste Höhe + 2 mm, auf
 *  Zehntel aufgerundet und in die Grenzen des Katalogs gelegt. */
export function kopfVorschlag(yMm: number, k: LogoKontext): number {
  const roh = Math.ceil((yMm + k.hoeheMm + 2) * 10 - 1e-9) / 10;
  return Math.min(Math.max(roh, k.kopfMinMm), k.kopfMaxMm);
}

/**
 * Füllt den Kopfbereich, den die freie Lage zur PFLICHT macht (Whitelist): Seite 1 und —
 * ohne „Logo nur auf Seite 1“ — die Folgeseiten. Nur, was FEHLT; ein eigener Wert bleibt.
 */
export function mitKopfVorschlag(entwurf: Entwurf, k: LogoKontext): Entwurf {
  const lage = logoLage(entwurf);
  if (lage === null) return entwurf;
  const vorschlag = kopfVorschlag(lage.yMm, k);
  let neu = entwurf;
  if (wertAn(neu, ["kopfbereich", "ersteSeiteMm"]) === undefined) {
    neu = mitWert(neu, ["kopfbereich", "ersteSeiteMm"], vorschlag);
  }
  if (wertAn(neu, ["logo", "nurSeite1"]) !== true
      && wertAn(neu, ["kopfbereich", "folgeseitenMm"]) === undefined) {
    neu = mitWert(neu, ["kopfbereich", "folgeseitenMm"], vorschlag);
  }
  return neu;
}

/**
 * Setzt x und/oder y des Logos. Beim ERSTEN Setzen (noch kein x/y) kommt der fehlende Teil
 * aus dem Vorschlag — x und y gibt es nur zusammen —, und der Pflicht-Kopfbereich wird
 * vorgeschlagen, falls er fehlt (Brief A7, Schritt 2).
 *
 * `mitKopf: false` beim TIPPEN (Fixrunde A7, W1): Der Vorschlag füllt nur einen leeren
 * Kopfbereich — rechnete er schon beim ersten Zeichen, fröre „4“ statt „40“ den Kopfbereich
 * ein. Das Register wendet ihn beim Verlassen des Felds an (`mitKopfVorschlag`).
 */
export function mitLogoLage(
  entwurf: Entwurf, lage: { xMm?: number; yMm?: number }, k: LogoKontext, mitKopf = true,
): Entwurf {
  const xAlt = wertAn(entwurf, ["logo", "xMm"]);
  const yAlt = wertAn(entwurf, ["logo", "yMm"]);
  const x = lage.xMm ?? (xAlt !== undefined ? xAlt : k.vorschlag.xMm);
  const y = lage.yMm ?? (yAlt !== undefined ? yAlt : k.vorschlag.yMm);
  const neu = mitWert(mitWert(entwurf, ["logo", "xMm"], x), ["logo", "yMm"], y);
  return mitKopf ? mitKopfVorschlag(neu, k) : neu;
}

/** „Wie bisher“: Lage und Größe weg, das Logo steht wieder im Textfluss. Der Kopfbereich
 *  bleibt (er wirkt auch ohne Logo als oberer Rand), ebenso „nur auf Seite 1“. */
export function ohneLogoLage(entwurf: Entwurf): Entwurf {
  let neu = mitWert(entwurf, ["logo", "xMm"], undefined);
  neu = mitWert(neu, ["logo", "yMm"], undefined);
  return mitWert(neu, ["logo", "prozent"], undefined);
}

/**
 * Begrenzt eine gezogene Lage auf Blatt und Kopfbereich (Brief A7, Schritt 4): x + Kante ≤
 * 210; das Logo endet über dem Kopfbereich jeder Seite, auf der es steht (Seite 1, ohne
 * „nur Seite 1“ auch die Folgeseiten). Fehlt der Kopfbereich noch, folgt ihm der Vorschlag
 * (y + Höhe + 2 mm ≤ Höchstwert).
 */
export function logoBegrenzt(
  entwurf: Entwurf, xMm: number, yMm: number, kanteMm: number, k: LogoKontext,
): { xMm: number; yMm: number } {
  const unterkante = (seite: KopfSeite): number =>
    kopfbereichMm(entwurf, seite) ?? k.kopfMaxMm - 2;
  let yMax = Math.min(297, unterkante("ersteSeiteMm")) - k.hoeheMm;
  if (wertAn(entwurf, ["logo", "nurSeite1"]) !== true) {
    yMax = Math.min(yMax, unterkante("folgeseitenMm") - k.hoeheMm);
  }
  return { xMm: zehntelIn(xMm, 210 - kanteMm), yMm: zehntelIn(yMm, yMax) };
}

/** Auf Zehntel in [0, max] — gerundet wird nie über `max` hinaus (sonst stünde das Logo
 *  um Hundertstel im Text, und der Server lehnte ab). */
function zehntelIn(wert: number, max: number): number {
  const grenze = Math.max(0, max);
  const rund = aufZehntel(Math.min(Math.max(wert, 0), grenze));
  return rund > grenze + 1e-9 ? Math.floor(grenze * 10 + 1e-9) / 10 : rund;
}

// ---------------------------------------------------------------------------
// Fuß in Spalten, Kopf-/Fußbereich (Belegdesign Etappe 2, Aufgabe 8)
// ---------------------------------------------------------------------------

/** Eine Fußspalte, wie sie im Entwurf steht: Breite als Text „33.3%“, Text mit Platzhaltern. */
export type FussSpalte = { breite: string; text?: string };

/** Die Fußspalten des Entwurfs (`fusszeile.spalten`) oder null, wenn der Schlüssel fehlt oder
 *  keine Liste ist (dann gilt der Systemfuß). Ungültige Einträge werden zu leeren Spalten —
 *  die Whitelist nennt am Feld, was fehlt. */
export function fussSpaltenAus(entwurf: Entwurf): FussSpalte[] | null {
  const liste = wertAn(entwurf, ["fusszeile", "spalten"]);
  if (!Array.isArray(liste)) return null;
  return liste.map((e: unknown) => {
    const o = istObjekt(e) ? e : {};
    return { breite: typeof o.breite === "string" ? o.breite : "",
             ...(typeof o.text === "string" ? { text: o.text } : {}) };
  });
}

/** Die Voreinstellung für 2 oder 3 Spalten aus dem Katalog (frische Kopie). */
export function fussVoreinstellung(
  katalog: { dreiSpalten: readonly FussSpalte[]; zweiSpalten: readonly FussSpalte[] },
  anzahl: 2 | 3,
): FussSpalte[] {
  return (anzahl === 3 ? katalog.dreiSpalten : katalog.zweiSpalten).map((k) => ({ ...k }));
}

const FUSS_BREITE = /^\s*([0-9]{1,3}(?:\.[0-9]{1,4})?)\s*%\s*$/;

/** Die Breite einer Fußspalte als Zahl (Prozent) oder null, wenn sie keine gültige Zahl ist. */
export function fussBreite(spalte: FussSpalte): number | null {
  const treffer = FUSS_BREITE.exec(spalte.breite);
  return treffer === null ? null : Number(treffer[1]);
}

/** Summe aller Fußbreiten in % (Zehntausendstel, exakt wie die Whitelist); null, wenn eine
 *  Breite keine gültige Zahl ist. */
export function fussSumme(spalten: readonly FussSpalte[]): number | null {
  let z = 0;
  for (const s of spalten) {
    const b = fussBreite(s);
    if (b === null) return null;
    z += Math.round(b * 10000);
  }
  return z / 10000;
}

/**
 * Setzt die Breite einer Spalte und gleicht die Summe aus (Entscheid 75): die RECHTS
 * anliegende Spalte nimmt den Rest auf, bei der ganz rechten die ganz linke. ABSOLUT gerechnet —
 * Nachbarin = 100 − Summe der übrigen Breiten (samt neuem Wert) —, nicht als Unterschied zum
 * vorigen Stand: So endet jede Tastenfolge bei 100 %, auch nach Zwischenständen und Korrekturen.
 * Der neue Wert und das Ergebnis werden auf Zehntel gerundet; gerechnet wird in
 * Zehntausendsteln (kein Gleitkomma-Rest). Der alte Wert der Nachbarin zählt nicht (ein
 * kaputter wird repariert). Bleiben für die Nachbarin weniger als 1 % oder mehr als 98 %,
 * bleibt sie unberührt (die Summenzeile zeigt dann den Befund) — wie `mitAusgleich`.
 */
export function mitFussAusgleich(breiten: readonly number[], index: number, neu: number):
    number[] {
  const z = breiten.map((b) => Math.round(b * 10000));
  const eigen = Math.round(neu * 10) * 1000;
  const nachbar = index === breiten.length - 1 ? 0 : index + 1;
  if (index < 0 || index >= z.length || nachbar === index) return [...breiten];
  const aus = [...z];
  aus[index] = eigen;
  const uebrige = aus.reduce((summe, v, i) => (i === nachbar ? summe : summe + v), 0);
  const rest = Math.round((1_000_000 - uebrige) / 1000) * 1000;
  if (rest >= 10_000 && rest <= 980_000) aus[nachbar] = rest;
  return aus.map((v) => v / 10000);
}

/**
 * Eine Eingabe in das Breitenfeld einer Fußspalte (Text, Komma oder Punkt): setzt die Breite
 * und gleicht aus (`mitFussAusgleich`). Eine leere oder unvollständige Eingabe („“, „abc“)
 * lässt die Nachbarin unverändert; ein Zwischenstand wie „33.“ gilt als die Zahl davor. Gleicht
 * auch nicht aus, wenn eine DRITTE Spalte keine gültige Breite hat. Mehr als eine
 * Nachkommastelle wird auf Zehntel gerundet.
 */
export function mitFussBreite(spalten: readonly FussSpalte[], index: number, roh: string):
    FussSpalte[] {
  const text = roh.trim().replace(",", ".");
  const kopie = spalten.map((s) => ({ ...s }));
  if (index < 0 || index >= kopie.length) return kopie;
  const alsZahl = /^[0-9]*\.?[0-9]*$/.test(text) && text !== "" && text !== "."
    ? Number(text) : null;
  const eigen = alsZahl === null || /^[0-9]*\.?[0-9]?$/.test(text)
    ? text : String(Math.round(alsZahl * 10) / 10);
  kopie[index] = { ...kopie[index]!, breite: eigen === "" ? "" : `${eigen}%` };
  if (alsZahl === null) return kopie;
  const nachbar = index === kopie.length - 1 ? 0 : index + 1;
  if (nachbar === index) return kopie;
  const alt = spalten.map(fussBreite);
  // Die Nachbarin wird überschrieben, ihr alter Wert zählt nicht; die übrigen müssen gelten.
  if (alt.some((b, i) => b === null && i !== index && i !== nachbar)) return kopie;
  const breiten = alt.map((b, i) => (i === index || i === nachbar ? (b ?? 0) : b!));
  const neu = mitFussAusgleich(breiten, index, alsZahl);
  // Unverändert (Grenzen 1–98 verletzt): nichts hineinschreiben, auch kein „0“ für einen
  // ungültigen Altwert.
  if (neu[nachbar] !== breiten[nachbar]) {
    kopie[nachbar] = { ...kopie[nachbar]!, breite: `${neu[nachbar]}%` };
  }
  return kopie;
}

/** Schreibt die Spalten (null: entfernt `fusszeile.spalten` — Systemfuß). */
export function mitFussSpalten(entwurf: Entwurf, spalten: readonly FussSpalte[] | null):
    Entwurf {
  return mitWert(entwurf, ["fusszeile", "spalten"], spalten === null ? undefined : spalten);
}

/**
 * Stellt die Spaltenzahl ein (2 oder 3 = Voreinstellung, null = Systemfuß). Beim EINSCHALTEN
 * schlägt der Editor den Fußbereich der ersten Seite vor, falls er fehlt: Ein Firmenfuß im
 * Drittel braucht dort etwa 32 mm (gemessen in rechnungslayout), sonst schnitte die Engine ihn
 * ab.
 */
export function mitFussSpaltenzahl(
  entwurf: Entwurf, anzahl: 2 | 3 | null,
  katalog: { dreiSpalten: readonly FussSpalte[]; zweiSpalten: readonly FussSpalte[];
             fussbereichMm: number },
): Entwurf {
  if (anzahl === null) return mitFussSpalten(entwurf, null);
  const einschalten = fussSpaltenAus(entwurf) === null;
  let neu = mitFussSpalten(entwurf, fussVoreinstellung(katalog, anzahl));
  if (einschalten && wertAn(neu, ["fussbereich", "ersteSeiteMm"]) === undefined) {
    neu = mitWert(neu, ["fussbereich", "ersteSeiteMm"], katalog.fussbereichMm);
  }
  return neu;
}

export type FussLinien = {
  /** Die Spaltengrenzen in mm ab der linken Blattkante (leer ohne Fußspalten). */
  spaltenMm: number[];
  /** Die Oberkante des Fußbereichs in mm ab der OBEREN Blattkante je Seitentyp; null =
   *  nicht gezeigt. */
  oberkanteMm: { erste: number | null; folge: number | null };
};

/**
 * Die Hilfslinien des Fußes (Entscheid 75): die Grenzen zwischen den Fußspalten über die
 * Satzspiegelbreite (210 − Rand links − Rand rechts) und die Oberkante des Fußbereichs je
 * Seitentyp (297 − Fußbereich; ohne eigenen Wert der Rand unten). Gezeigt wird nur, was der
 * Entwurf setzt: Spalten, oder ein eigener Fußbereich je Seitentyp; mit Spalten beide Seitentypen.
 * Null, wenn nichts zu zeigen ist.
 */
export function fussLinien(
  entwurf: Entwurf, raender: { links: number; rechts: number; unten: number },
): FussLinien | null {
  const spalten = fussSpaltenAus(entwurf);
  const breite = 210 - raender.links - raender.rechts;
  const spaltenMm = spalten === null || breite <= 0 ? []
    : spaltenLinien(spalten).map((p) => raender.links + p * breite / 100);
  const hoehe = (seite: "ersteSeiteMm" | "folgeseitenMm"): number | null => {
    const eigen = zahlOderNull(wertAn(entwurf, ["fussbereich", seite]));
    return eigen ?? (spalten === null ? null : raender.unten);
  };
  const kante = (h: number | null): number | null => (h === null ? null : 297 - h);
  const erste = kante(hoehe("ersteSeiteMm"));
  const folge = kante(hoehe("folgeseitenMm"));
  return spaltenMm.length === 0 && erste === null && folge === null ? null
    : { spaltenMm, oberkanteMm: { erste, folge } };
}

/** Ein Eintrag von `von` nach `nach`; ausserhalb der Grenzen bleibt alles, wie es ist. */
export function verschoben<T>(liste: readonly T[], von: number, nach: number): T[] {
  const kopie = [...liste];
  if (von < 0 || von >= kopie.length || nach < 0 || nach >= kopie.length) return kopie;
  const [eintrag] = kopie.splice(von, 1);
  kopie.splice(nach, 0, eintrag as T);
  return kopie;
}

/**
 * Steht eine Grafik auf dieser Seite des Blatts? Seitenwahl wie in rechnungslayout
 * (R-MECHANIK); den Unterschriftsplatz gibt es nur auf seinen Belegarten.
 */
export function grafikAufSeite(
  g: { kind: string; pages: string; documentKinds: readonly string[] },
  seite: number, belegart: string,
): boolean {
  const seiteOk = g.pages === "ALL" || (g.pages === "FIRST" ? seite === 1 : seite > 1);
  return seiteOk && (g.kind !== "SIGNATURE" || g.documentKinds.includes(belegart));
}

/** Der Anzeigename aus dem Dateinamen: ohne Endung, ohne die Zeichen, die der Server
 *  ablehnt (R-GRAFIKNAME), höchstens 80 Zeichen; bleibt nichts, heisst sie „Grafik". */
export function grafikNameAus(dateiname: string): string {
  const name = dateiname.replace(/\.(svg|png|jpe?g)$/i, "").replace(/[<>{}"]/g, "").trim().slice(0, 80);
  return name === "" ? "Grafik" : name;
}

/** Die Reiter der Gestaltung (Spec, Oberfläche, rechts). */
export type RegisterId = "marke" | "seite" | "sektionen" | "tabelle" | "bloecke" | "texte";

/**
 * Feldpfad → Reiter (Paket „Editor-Arbeitsdialog“, ohne Entscheid): Der Server meldet seine
 * Befunde je Pfad (`seitenraender.oben`, `positionsspalten[1].titel`); der Fehlerzähler am
 * Reiter braucht sie je Reiter. Die Zuordnung hängt am ersten Pfadglied — dieselben Präfixe,
 * die die Register beim Schreiben benutzen. Ein unbekanntes Präfix zählt an keinem Reiter
 * (die Meldung oben nennt es trotzdem).
 */
export const REITER_JE_PFADPRAEFIX: Readonly<Record<string, RegisterId>> = {
  grafiken: "marke",
  logo: "marke",
  // Kopf- und Fußbereich, Tabellenbeginn und Fußspalten haben ihre Felder im Register Seite
  // (Aufgabe 8) — dort zählen auch ihre Befunde. Das Register Marke zeigt den Kopplungsbefund
  // „Logo ragt in den Text“ (kopfbereich.*) zusätzlich unter dem Logo, zählt ihn aber nicht.
  kopfbereich: "seite",
  fussbereich: "seite",
  positionstabelle: "seite",
  fusszeile: "seite",
  seitenraender: "seite",
  sektionen: "sektionen",
  zeilenlayout: "tabelle",
  positionsspalten: "tabelle",
  bloecke: "bloecke",
  belegarten: "texte",
  texte: "texte",
};

/** Der Reiter eines Befund-Pfads (ein vorangestelltes `layout.` zählt nicht) oder `null`. */
export function reiterFuer(pfad: string): RegisterId | null {
  const praefix = pfad.replace(/^layout\./, "").split(/[.[]/)[0] ?? "";
  return Object.hasOwn(REITER_JE_PFADPRAEFIX, praefix) ? REITER_JE_PFADPRAEFIX[praefix]! : null;
}

/** Die Zahl der Feldfehler je Reiter (nur Reiter mit Fehlern stehen darin). */
export function fehlerJeReiter(fehler: Record<string, string>): Partial<Record<RegisterId, number>> {
  const zaehler: Partial<Record<RegisterId, number>> = {};
  for (const pfad of Object.keys(fehler)) {
    const reiter = reiterFuer(pfad);
    if (reiter !== null) zaehler[reiter] = (zaehler[reiter] ?? 0) + 1;
  }
  return zaehler;
}
